import React, { useEffect, useState, useMemo } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  getSelectedItemFromPath,
  getSprintIdFromPath,
  getViewTypeFromPath,
  getBacklogViewTypeFromPath,
} from "./utils";
import { useAppSelector, useAppDispatch } from "@/app/hooks";
import { toast } from "react-toastify";

// Components
import BoardSidebar from "./components/BoardSidebar";
import BoardContent from "./components/BoardContent";
import BoardModals from "./components/BoardModals";
import TaskLayout from "./components/TaskLayout";

// Hooks
import { useBoardInitialization } from "./components/custom-hooks/useBoardInitialization";
import { useBoardEffects } from "./components/custom-hooks/useBoardEffects";
import { useColumnOperations } from "./components/custom-hooks/useColumnOperations";
import { useTaskOperations } from "./components/custom-hooks/useTaskOperations";
import { useBoardFilters } from "./components/custom-hooks/useBoardFilters";

// API Hooks
import {
  useGetListOfBugsQuery,
  useGetListOfStatusQuery,
  useGetProjectByIdQuery,
  useGetListOfTagsQuery,
  useGetSprintListQuery,
  useGetListOfMembersQuery,
} from "../../features/bughurd/api";
import { useGetCompanyMembersQuery } from "@/features/Members/api";
import { useGetListOfProjectsQuery } from "@/features/bughurd/api";

// Selectors and Actions
import {
  selectBoardState,
  selectSelectedItem,
  selectViewType,
  selectGroupingConfig,
  selectDebouncedSearchQuery,
  selectPagination,
  selectLoadingStates,
  selectActiveSprintIds,
} from "@/features/bughurd/boardSelector";
import {
  setSelectedItem,
  setDrawerOpen,
  resetBoardState,
  clearAllFilters,
  setGroupBy,
  setShowGroupOptions,
  setSubGroupBy,
  setPagination,
  setStatusList,
  setProjectDetails,
  setProjects,
  setListOfBugs,
  setListCount,
  setTagList,
  setCompanyMemberData,
  setIsBugsLoading,
  setIsBugsFetching,
  setIsRecurring,
  setActiveSprintIds,
  setActiveSprints,
  setAllSprints,
  setBacklogViewType,
  setViewType,
  setSprintData,
} from "@/features/bughurd/slice";

import RetrospectivesTab from "./components/RetrospectivesTab";
import PMOHubDashboard from "./components/PMODashboard/PMOHubDashboard";
import SystemBug from "./components/SystemBug";
import ApplicationList from "./components/ApplicationList";

type BoardPageProps = {
  reactFlow: any;
  parentData?: any;
};

const BoardPage: React.FC<BoardPageProps> = ({ reactFlow, parentData }) => {
  const { id, sprintId } = useParams<{ id: string; sprintId?: string }>();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation(["bugherd"]);
  const dispatch = useAppDispatch();

  // State to store selected sprint data
  const [selectedSprintData, setSelectedSprintData] = useState<any>(null);

  // Get current user from appGlobal
   const { currentUser } = useAppSelector((state) => state.appGlobal);

  // Fetch project members to determine if current user is a client
  const { data: projectMembersData } = useGetListOfMembersQuery(
    { projectId: id, page: 1, size: 100, searchName: "" },
    { skip: !id }
  );

  // Determine if the current user is a client based on project members data
  const isCurrentUserClient = useMemo(() => {
    const projectMembers = projectMembersData?.data?.decryptedData?.items || [];
    const currentUserId = currentUser?._id;

    if (!currentUserId || projectMembers.length === 0) {
      return false;
    }

    // Find the current user in project members
    const currentUserMember = projectMembers.find(
      (member: any) => member?.user?._id === currentUserId || member?._id === currentUserId
    );

    // Check if user role is CLIENT
    return currentUserMember?.userRole === 'CLIENT';
  }, [projectMembersData?.data?.decryptedData?.items, currentUser?._id]);

  // Sync URL to Redux state on mount and pathname changes
  useEffect(() => {
    const selectedItemFromUrl = getSelectedItemFromPath(pathname);
    const viewTypeFromUrl = getViewTypeFromPath(pathname);
    const backlogViewTypeFromUrl = getBacklogViewTypeFromPath(pathname);

    // Update Redux state based on URL
    if (selectedItemFromUrl) {
      dispatch(setSelectedItem(selectedItemFromUrl));
    }

    // Set view types based on URL
    if (viewTypeFromUrl) {
      dispatch(setViewType(viewTypeFromUrl));
    }
    if (backlogViewTypeFromUrl) {
      dispatch(setBacklogViewType(backlogViewTypeFromUrl));
    }
  }, [pathname, dispatch]);

  // Redirect CLIENT users to Feedback tab if they land on other pages
  useEffect(() => {
    if (isCurrentUserClient && id && !pathname.includes('/feedback')) {
      navigate(`/defect-tracker/projects/${id}/feedback`, { replace: true });
      dispatch(setSelectedItem('Feedback'));
    }
  }, [isCurrentUserClient, id, pathname, navigate, dispatch]);

  // Selectors
  const boardState = useAppSelector(selectBoardState);
  const selectedItem = useAppSelector(selectSelectedItem);
  const viewType = useAppSelector(selectViewType);
  const backlogViewType = useAppSelector(
    (state) => state?.board?.backlogViewType
  );
  const groupingConfig = useAppSelector(selectGroupingConfig);
  const debouncedSearchQuery = useAppSelector(selectDebouncedSearchQuery);
  const pagination = useAppSelector(selectPagination);
  const loadingStates = useAppSelector(selectLoadingStates);
  const activeSprintIds = useAppSelector(selectActiveSprintIds);
  const { roleCodeDetails: myPermissions } = useAppSelector(
    (state) => state.appGlobal
  );

  const { groupBy, subGroupBy } = groupingConfig;
  const { drawerOpen } = boardState;

  // Custom hooks for initialization and effects
  const {
    isMyTaskView,
    statusList,
    projectDetails,
    listOfBugs,
    tagList,
    companyMemberData,
    projects,
    // currentUser,
  } = useBoardInitialization(id);
  const { filters } = useBoardFilters();

  // Column operations
  const {
    addColumn,
    handleColumnTitleChange,
    handleColumnDeleteRequest,
    handleColumnDelete,
    handleDeleteConfirmClose,
    isCreatingStatus,
    isDeletingStatus,
  } = useColumnOperations(id, isMyTaskView);

  // Task operations
  const {
    handleTaskUpdate,
    handleLoadMore,
    handlePageChange,
    refreshColumnData,
  } = useTaskOperations(id, isMyTaskView);

  const { recordsPerPage } = useAppSelector((state) => state.appGlobal);

  const PAGE_LIMIT =
    viewType !== "kanban" && recordsPerPage ? recordsPerPage : 10;

  const {
    data: projectDetailsData,
    isLoading: isProjectLoading,
    refetch: refetchProjectDetails,
  } = useGetProjectByIdQuery(id!, { skip: !id });
  const boardType = projectDetailsData?.data?.decryptedData?.boardType;

  // Redirect based on project preferences (isBoard, isFeedback, isLiveLog)
  const isSupport = projectDetailsData?.data?.decryptedData?.configurationType === "SUPPORT" || false;
  const baseUrl = window?.location?.origin;
  const isPmoHub = baseUrl?.includes("pmohub") || false;

  useEffect(() => {
    if (!id || isCurrentUserClient) return;
    const pref = projectDetailsData?.data?.decryptedData?.preference;
    if (!pref) return; // No preference set (old projects) — keep default behavior

    const boardType = projectDetailsData?.data?.decryptedData?.boardType;

    // Determine the default route based on priority: Board > Feedback > Live Logs
    const getDefaultPath = () => {
      if (pref.isBoard) return `/defect-tracker/projects/${id}/dashboard`;
      if (isSupport && !isPmoHub && pref.isFeedback) return `/defect-tracker/projects/${id}/feedback`;
      if (isSupport && !isPmoHub && pref.isLiveLog) return `/defect-tracker/projects/${id}/system-bugs`;
      return `/defect-tracker/projects/${id}/dashboard`;
    };

    // Routes that require isBoard
    const isBoardRoute =
      pathname.includes("/dashboard") ||
      pathname.includes("/task-board") ||
      pathname.includes("/backlog") ||
      pathname.includes("/sprints") ||
      pathname.includes("/retrospectives") ||
      pathname.includes("/appearance") ||
      pathname.includes("/recurring-tasks") ||
      pathname.includes("/tags");

    // Routes that require isFeedback
    const isFeedbackRoute = pathname.includes("/feedback");

    // Routes that require isLiveLog
    const isLiveLogRoute =
      pathname.includes("/system-bugs") ||
      pathname.includes("/sniffer-autobug");

    // Routes that require applications
    const isApplicationsRoute = pathname.includes("/applications");

    const shouldRedirect =
      (isBoardRoute && pref.isBoard === false) ||
      (isFeedbackRoute && (pref.isFeedback === false || !isSupport || isPmoHub)) ||
      (isLiveLogRoute && (pref.isLiveLog === false || !isSupport || isPmoHub)) ||
      (isApplicationsRoute && isPmoHub);

    if (shouldRedirect) {
      navigate(getDefaultPath(), { replace: true });
    }
  }, [projectDetailsData, id, pathname, navigate, isCurrentUserClient, isSupport, isPmoHub]);

  // Initialize SCRUM board to show All Sprints view
  // useEffect(() => {
  // if (
  //   boardType === "SCRUM" &&
  //   !selectedItem?.toLowerCase().includes("sprint")
  // ) {
  //   dispatch(setSelectedItem("All Sprints"));
  //   dispatch(setBacklogViewType("allSprint"));
  // }
  // dispatch(setBacklogViewType(selectedItem));
  // dispatch(setViewType(selectedItem));
  // console.log("selectedItem from BoardPage", selectedItem);
  // }, [boardType, selectedItem]);

  const {
    data: sprintListData,
    isLoading: isSprintLoading,
    refetch: refetchSprints,
    isFetching: isSprintFetching,
  } = useGetSprintListQuery(
    {
      projectId: id || "",
      page: 1,
      limit: 100,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !id,
    }
  );
  // Effect to handle activeSprintIds based on boardType
  useEffect(() => {
    if (boardType === "SCRUM" && sprintListData?.data?.decryptedData?.items) {
      const activeSprints = sprintListData.data.decryptedData.items.filter(
        (item: any) => item.status === "ACTIVE"
      );
      const activeIds = activeSprints.map((item: any) => item._id);
      dispatch(setActiveSprintIds(activeIds));
      dispatch(setActiveSprints(activeSprints));
      dispatch(setAllSprints(sprintListData?.data?.decryptedData?.items));
    } else if (boardType === "KANBAN") {
      // Clear activeSprintIds for Kanban board
      dispatch(setActiveSprintIds([]));
      dispatch(setActiveSprints([]));
      dispatch(setAllSprints([]));
    }
  }, [boardType, sprintListData, dispatch]);

  // Populate selectedSprintData from URL when page is loaded/refreshed directly on a sprint route
  useEffect(() => {
    if (!selectedItem?.startsWith("Sprint:")) return;
    const sprintIdFromItem = selectedItem.replace("Sprint:", "");

    // Try finding in API data first
    const apiSprints = sprintListData?.data?.decryptedData?.items || [];
    let matched = apiSprints.find((s: any) => s._id === sprintIdFromItem);

    // Fallback: check Redux allSprints state (populated from previous fetches)
    if (!matched && boardState.allSprints?.length > 0) {
      matched = boardState.allSprints.find((s: any) => s._id === sprintIdFromItem);
    }

    if (matched) {
      setSelectedSprintData(matched);
      dispatch(setSprintData(matched));
    }
  }, [selectedItem, sprintListData, boardState.allSprints]);

  // API Queries with skip conditions
  const shouldFetchStatus =
    selectedItem === "Task Board" ||
    selectedItem === "My Tasks" ||
    isMyTaskView ||
    selectedItem?.toLowerCase().includes("sprint");

  const skipStatusRefetch = !shouldFetchStatus;

  const {
    data: statusListData,
    isLoading: isStatusLoading,
    isFetching: isStatusFetching,
    refetch: refetchStatusList,
  } = useGetListOfStatusQuery(
    { projectId: isMyTaskView ? undefined : id, sprint: activeSprintIds },
    {
      skip: skipStatusRefetch,
      refetchOnMountOrArgChange: true,
    }
  );

  const { data: projectsData } = useGetListOfProjectsQuery({
    page: 1,
    size: 100,
  });
  const {
    selectedTags,
    selectedAvatars,
    selectedStatuses,
    selectedPriorities,
    selectedProjects,
    dueDateStart,
    dueDateEnd,
    startDateStart,
    startDateEnd,
    updatedAtStart,
    updatedAtEnd,
    isRecurring,
    selectedCreatedBy,
  } = filters;

  // Helper function to get filtered projects for MyTaskView
  const getFilteredProjects = () => {
    // if (!isMyTaskView) return undefined;

    if (isMyTaskView && selectedProjects && selectedProjects.length > 0) {
      return selectedProjects;
    }
    return undefined;

    // return projects?.map((item) => item._id);
  };
  const shouldFetchBugs = (() => {
    if (selectedItem === "Retrospectives") return false;
    if (
      ["My Tasks", "Task Board"].includes(selectedItem) &&
      boardType === "KANBAN"
    )
      return true;
    if (
      boardType === "SCRUM" &&
      (selectedItem === "Backlog" ||
        selectedItem?.toLowerCase().includes("sprint"))
    )
      return true;
    if (boardType === "SCRUM" && selectedItem === "All Sprints") return true;
    return false;
  })();
  const {
    data: listOfBugsData,
    isLoading: isBugsLoadingQuery,
    isFetching: isBugsFetchingQuery,
    refetch: refetchBugList,
  } = useGetListOfBugsQuery(
    {
      id: isMyTaskView ? "" : id || "",
      forBoard: true,
      search: debouncedSearchQuery || "",
      assignTo: isMyTaskView
        ? [currentUser?._id]
        : selectedAvatars?.length > 0
        ? selectedAvatars
        : undefined,
      tags: selectedTags?.length > 0 ? selectedTags : undefined,
      statuses: selectedStatuses?.length > 0 ? selectedStatuses : undefined,
      severities:
        selectedPriorities?.length > 0 ? selectedPriorities : undefined,
      createdBy: selectedCreatedBy?.length > 0 ? selectedCreatedBy : undefined,
      page: pagination["global"]?.page || 1,
      limit: pagination["global"]?.limit || PAGE_LIMIT,
      ...(dueDateStart && { dueDateStart }),
      ...(dueDateEnd && { dueDateEnd }),
      ...(startDateStart && { startDateStart }),
      ...(startDateEnd && { startDateEnd }),
      ...(updatedAtStart && { updatedAtStart }),
      ...(updatedAtEnd && { updatedAtEnd }),
      ...(isRecurring && { isRecurring }),
      ...(selectedItem === "Recurring Tasks" && { isRecurring: true }),
      ...(isMyTaskView && {
        project: getFilteredProjects(),
      }),

      ...(boardType === "SCRUM" && selectedSprintData
        ? { sprint: [selectedSprintData?._id] }
        : boardType === "SCRUM" &&
          selectedItem === "All Sprints" &&
          activeSprintIds.length > 0
        ? { sprint: activeSprintIds }
        : {}),
    },
    {
      skip: (() => {
        if (isMyTaskView || selectedItem === "My Tasks") return false;
        const isSprintView =
          selectedItem === "All Sprints" ||
          selectedItem?.toLowerCase().includes("sprint");
        if (boardType === "SCRUM") {
          const needsKanbanFetch =
            backlogViewType === "kanban" ||
            backlogViewType === "list" ||
            (backlogViewType === "table" && groupBy !== "None");
          return !isSprintView || needsKanbanFetch;
        }
        // For KANBAN
        return (
          viewType === "kanban" ||
          viewType === "list" ||
          (viewType === "table" && groupBy !== "None") ||
          (selectedItem !== "Task Board" && selectedItem !== "My Tasks")
        );
      })(),
      // skip: !shouldFetchBugs,
    }
  );

  const { data: tagListData } = useGetListOfTagsQuery(
    isMyTaskView ? "" : id || ""
  );
  const { data: companyMemberDataQuery } = useGetCompanyMembersQuery({});
  // Determine loading state
  const isInitialLoading = isProjectLoading || (isStatusLoading && !statusList);
  useBoardEffects(id, selectedItem, isMyTaskView, refetchStatusList);
  // useEffect(() => {
  //   if (isMyTaskView) {
  //     // 1. Clear the stale SCRUM board data
  //     dispatch(setProjectDetails(null));

  //     // 2. Ensure the view is set to Kanban/My Tasks
  //     dispatch(setSelectedItem("My Tasks"));
  //     dispatch(setViewType("kanban"));
  //   }
  // }, [isMyTaskView, dispatch]);
  useEffect(() => {
    if (statusListData?.data?.decryptedData?.items) {
      if (isMyTaskView) {
        const listWithTasks = statusListData.data.decryptedData.items.filter(
          (item: any) => {
            return item.bugCount > 0;
          }
        );
        dispatch(setStatusList(listWithTasks));
      } else {
        dispatch(setStatusList(statusListData.data.decryptedData.items));
      }
    }
  }, [statusListData]);

  useEffect(() => {
    if (projectDetailsData?.data?.decryptedData) {
      dispatch(
        setProjectDetails(projectDetailsData?.data?.decryptedData)
      );
    }
  }, [projectDetailsData]);

  useEffect(() => {
    if (projectsData?.data?.decryptedData?.items) {
      dispatch(setProjects(projectsData.data.decryptedData.items));
    }
  }, [projectsData]);
  useEffect(() => {
    if (listOfBugsData?.data?.decryptedData?.items) {
      dispatch(setListOfBugs(listOfBugsData.data.decryptedData.items));
      dispatch(setListCount(listOfBugsData.data.decryptedData.total || 0));
    }
  }, [listOfBugsData]);

  useEffect(() => {
    if (tagListData?.data?.decryptedData?.items) {
      dispatch(setTagList(tagListData.data.decryptedData.items));
    }
  }, [tagListData]);

  useEffect(() => {
    if (companyMemberDataQuery?.data?.decryptedData) {
      dispatch(
        setCompanyMemberData(companyMemberDataQuery.data.decryptedData)
      );
    }
  }, [companyMemberDataQuery]);

  useEffect(() => {
    dispatch(setIsBugsLoading(isBugsLoadingQuery));
  }, [isBugsLoadingQuery]);

  useEffect(() => {
    dispatch(setIsBugsFetching(isBugsFetchingQuery));
  }, [isBugsFetchingQuery]);

  useEffect(() => {
    if (groupBy === "None" && selectedItem === "Task Board") {
      dispatch(
        setPagination({
          key: "global",
          pagination: {
            page: 1,
            limit: PAGE_LIMIT,
          },
        })
      );
    }
  }, [groupBy, selectedItem, viewType, dispatch]);

  // ====================================================================
  // FIX: Refetch status list when backlogViewType changes
  // ====================================================================
  useEffect(() => {
    if (selectedItem === "Task Board") {
      refetchStatusList();
    }
  }, [id, selectedItem, isMyTaskView, refetchStatusList]);

  // Clear selected sprint data when switching away from sprint items
  useEffect(() => {
    if (selectedItem && !selectedItem.toLowerCase().includes("sprint")) {
      setSelectedSprintData(null);
    }
  }, [selectedItem]);

  // Event handlers
  const handleDrawerOpen = () => {
    dispatch(setDrawerOpen(true));
  };

  const handleDrawerClose = () => {
    dispatch(setDrawerOpen(false));
  };

  const handleGroupByChange = (value: string) => {
    if (value === groupBy) {
      return;
    }

    dispatch(resetBoardState());
    dispatch(clearAllFilters());
    dispatch(setGroupBy(value));
    dispatch(setShowGroupOptions(false));
    dispatch(setSubGroupBy("None"));

    toast.success(`Grouping changed to ${value}`);
  };

  // Updated handler to receive sprint data
  const handleSidebarItemSelect = (item: string, sprintData?: any) => {
    if (item === "Dashboard") {
      dispatch(setSelectedItem(item));
    }
    if (item === "All Sprints") {
      dispatch(setBacklogViewType("allSprint"));
      dispatch(setViewType("allSprint"));
    }
    if (item?.toLowerCase().includes("sprint") && item !== "All Sprints") {
      dispatch(setViewType("kanban"));
      dispatch(setBacklogViewType("kanban"));
    }
    if (item === "Task Board") {
      dispatch(setViewType("kanban"));
      dispatch(setBacklogViewType("kanban"));
    }
    if (item === "Backlog") {
      dispatch(setBacklogViewType("backlog"));
      // dispatch(setViewType("backlog"));
    }
    dispatch(setSelectedItem(item));

    // Store sprint data if provided
    if (sprintData) {
      dispatch(setSprintData(sprintData));
      setSelectedSprintData(sprintData);
    } else {
      setSelectedSprintData(null);
    }
  };
  // const handleSidebarItemSelect = (item: string, sprintData?: any) => {
  //   if (item === "All Sprints") {
  //     dispatch(setBacklogViewType("allSprint"));
  //     dispatch(setViewType("allSprint"));
  //   }
  //   if (item?.toLowerCase().includes("sprint") && item !== "All Sprints") {
  //     dispatch(setViewType("kanban"));
  //     dispatch(setBacklogViewType("kanban"));
  //   }
  //   if (item === "Task Board") {
  //     dispatch(setViewType("dashboard"));
  //     dispatch(setBacklogViewType("dashboard"));
  //   }
  //   if (item === "Backlog") {
  //     dispatch(setBacklogViewType("dashboard"));
  //     // dispatch(setViewType("backlog"));
  //   }

  //   // FIX: Set viewType to 'kanban' for My Tasks
  //   if (item === "My Tasks") {
  //     dispatch(setViewType("kanban"));
  //     dispatch(setBacklogViewType("kanban")); // Also update backlogViewType
  //   }

  //   dispatch(setSelectedItem(item));

  //   // Store sprint data if provided
  //   if (sprintData) {
  //     dispatch(setSprintData(sprintData));
  //     setSelectedSprintData(sprintData);
  //   } else {
  //     setSelectedSprintData(null);
  //   }
  // };

  const handleDrawerToggle = () => {
    if (drawerOpen) {
      handleDrawerClose();
    } else {
      handleDrawerOpen();
    }
  };

  const getLoadingStatus = () => {
    const keys = Object.keys(loadingStates.loadingStatuses);
    let isLoading = false;
    keys?.forEach((key) => {
      if (
        loadingStates.loadingStatuses?.[key]?.isInitialLoading ||
        loadingStates.loadingStatuses?.[key]?.isLoadingMore
      ) {
        isLoading = true;
      }
    });
    return isLoading;
  };

  return (
    <Box sx={{ display: "flex", position: "relative", overflow: "hidden", height: "100vh" }}>
      <BoardSidebar
        onItemSelect={handleSidebarItemSelect}
        onDrawerToggle={handleDrawerToggle}
        isCurrentUserClient={isCurrentUserClient}
      />
      {selectedItem === "Applications" || pathname.includes("/applications") ? (
        <Box sx={{ flex: 1, minWidth: 0, overflow: "auto" }}>
          <ApplicationList />
        </Box>
      ) : selectedItem === "Dashboard" ? (
        <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
          <PMOHubDashboard parentData={parentData} />
        </Box>
      ) : selectedItem === "Retrospectives" ? (
        <RetrospectivesTab reactFlow={reactFlow} parentData={parentData} />
      ) : selectedItem === "Live Logs" ? (
        <SystemBug parentData={parentData} />
      ) : (
        <TaskLayout
          refreshColumnData={refreshColumnData}
          refetchBugList={refetchBugList}
          refetchStatusList={refetchStatusList}
          showSubGrouping={viewType === "kanban"}
          handleGroupByChange={handleGroupByChange}
          refetchSprints={refetchSprints}
          selectedSprint={selectedSprintData}
          reactFlow={reactFlow}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              position: "relative",
            }}
          >
            {(isInitialLoading ||
              isStatusLoading ||
              isStatusFetching ||
              getLoadingStatus()) && (
              <Box
                sx={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(0, 0, 0, 0.2)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  zIndex: 1400,
                }}
              >
                <CircularProgress />
              </Box>
            )}
            <Box sx={{ overflowY: "hidden", height: "100%" }}>
              <BoardContent
                isInitialLoading={false}
                isMyTaskView={isMyTaskView}
                projectDetails={projectDetails}
                handlePageChange={handlePageChange}
                refreshColumnData={refreshColumnData}
                handleTaskUpdate={handleTaskUpdate}
                handleLoadMore={handleLoadMore}
                refetchBugList={refetchBugList}
                refetchStatusList={refetchStatusList}
                refetchProjectDetails={refetchProjectDetails}
                reactFlow={reactFlow}
                selectedSprint={selectedSprintData}
                allSprints={sprintListData?.data?.decryptedData?.items || []}
                parentData={parentData}
                isCurrentUserClient={isCurrentUserClient}
              />
            </Box>
          </Box>
        </TaskLayout>
      )}
      <BoardModals
        onDeleteConfirm={handleColumnDelete}
        onDeleteCancel={handleDeleteConfirmClose}
        isDeletingStatus={isDeletingStatus}
      />
    </Box>
  );
};

export default BoardPage;
